import React, {
  useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import Chip from '../chip/chip.component';
import StructureService from '../../services/structure-service';
import useStylesFilterPanel from './filter-panel.styles';
import { FILTER_KEY_ID } from '../../constants/system/filter-keys';
import POINTER_KEY_PRIMARY from '../../constants/system/pointer-keys';

const FilterPanel: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const FILTER_LABEL_KEY_DEFAULT: string = 'name';

  const {
    filters,
    filterOptionMap,
    filterKeyMap,
    filterLabelMap,
    filterLabelKeyMap,
    quickFilters,
    defaultLabelMap,
    onSetQuickFilterValue,
    onDeleteFilterValue,
  } = props;

  const { classes } = useStylesFilterPanel();

  const activeFilterMap: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => (
    StructureService.getItemMap(
      (Object.entries(filters as Record<string, FilterValue>))
        .filter(([filterType, filterValue]: [string, FilterValue]): boolean => (
          !!filterType && (!!filterValue || filterValue === false)
        ))
        .map(([filterType, filterValue]: [string, FilterValue]): StructureItem => {
          const filterOptionList: FilterOption[] = (filterOptionMap && filterOptionMap[filterType]) || [];
          const filterOptionKey: string = (filterKeyMap && filterKeyMap[filterType]) || FILTER_KEY_ID;
          const filterLabelKey: string = (filterLabelKeyMap && filterLabelKeyMap[filterType]) || null;
          const defaultSelectedValue: FilterOption = {
            id: 1,
            [filterLabelKey || FILTER_LABEL_KEY_DEFAULT]: ((defaultLabelMap && defaultLabelMap[filterType])
              || filterValue),
          };

          return {
            name: filterType,
            value: filterOptionList.length ? (filterOptionList.find((filterOptionItem: FilterOption): boolean => (
              filterOptionItem[filterOptionKey] === filterValue
            )) || null) : defaultSelectedValue,
          };
        })
        .filter((filter: StructureItem): boolean => {
          const filterLabelKey: string = (filter && filter.name && filter.value && filterLabelKeyMap
            && filterLabelKeyMap[filter.name]) || null;
          const filterMappedValue: FilterValue = (filterLabelKey && filter.value[filterLabelKey]) || null;

          return filter && !!filter.name && !!filter.value
            && (!!filterMappedValue || !!filter.value.title || !!filter.value.name);
        }),
      'name',
      'value',
    )
  ), [filters, filterOptionMap, filterKeyMap, filterLabelKeyMap]);

  const onDeleteClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    filterType: string,
  ): void => {
    if (event.button === POINTER_KEY_PRIMARY) {
      if (onDeleteFilterValue && filterType) {
        onDeleteFilterValue(filterType);
      }

      event.stopPropagation();
    }
  }, [filters]);

  return (
    <Fragment>
      {
        ((activeFilterMap && !!Object.keys(activeFilterMap).length) || (quickFilters && !!quickFilters.length)) && (
          <div className={classes.filterPanel}>
            {
              (!activeFilterMap || (activeFilterMap && !Object.keys(activeFilterMap).length))
              && (quickFilters && !!quickFilters.length) && (
                quickFilters.map((quickFilterItem: QuickFilterItem): ReactElement => (
                  <Chip
                    key={quickFilterItem.id}
                    label={quickFilterItem.title}
                    onClick={(): void => {
                      if (onSetQuickFilterValue) {
                        onSetQuickFilterValue(quickFilterItem.type, quickFilterItem.value);
                      }
                    }}
                  />
                ))
              )
            }
            {
              (activeFilterMap && !!Object.keys(activeFilterMap).length) && Object.entries(activeFilterMap)
                .map(([filterType, filterValue]: [string, StructureItem]): ReactElement => {
                  const filterOptionList: FilterOption[] = (filterOptionMap && filterOptionMap[filterType]) || [];
                  const useDefaultLabel: boolean = !filterOptionList.length && !!defaultLabelMap
                    && !!defaultLabelMap[filterType];
                  const filterLabel: string = filterLabelMap && filterLabelMap[filterType] && !useDefaultLabel
                    ? `${filterLabelMap[filterType]}: ` : '';
                  const filterValueKey: string | null = (filterLabelKeyMap && filterLabelKeyMap[filterType]) || null;
                  const filterMappedValue: string | null = (filterValueKey && filterValue[filterValueKey]) || null;

                  return (
                    <Chip
                      isHighlighted
                      withDelete
                      key={filterValue.id}
                      label={`${filterLabel}${filterMappedValue || filterValue.title || filterValue.name}`}
                      onDelete={(event: React.PointerEvent): void => {
                        onDeleteClick(event, filterType);
                      }}
                    />
                  );
                })
            }
          </div>
        )
      }
    </Fragment>
  );
});

export default FilterPanel;
